<template>
  <form @submit.prevent>
    <div>
      <b-card class="mb-3">
        <h2>
          <span v-if="isEditMode">{{ $t("audit-control.control.name") }} "{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
          <span v-else>{{ $t("audit-control.control.new") }}</span>
        </h2>

        <div class="row">
          <!-- Control No (Read Only) & Class -->
          <div class="col-md-6" v-if="form._id">
            <sweet-input disabled :value="form.no" layout="vertical" :isValid="true" :label="$t('audit-control.control.no')" readonly />
          </div>
          <div class="col-md-6" v-if="form._id">
            <div class="form-group">
              <label>{{ $t("audit-control.control.class") }}</label>
              <select disabled class="form-control" v-model="form.class" :class="{ 'is-invalid': $v.form.class.$error }">
                <option :value="undefined">{{ $t("common.select") }}</option>
                <option v-if="!allowedClass || allowedClass === 'COMPLIANCE_MANAGEMENT'" value="COMPLIANCE_MANAGEMENT">{{
                  $t("audit-control.class.compliance_management")
                }}</option>
                <option v-if="!allowedClass || allowedClass === 'CONTROL_ACTIVITY'" value="CONTROL_ACTIVITY">{{ $t("audit-control.class.control_activity") }}</option>
                <option v-if="!allowedClass || allowedClass === 'AUDIT_ACTIVITY'" value="AUDIT_ACTIVITY">{{ $t("audit-control.class.audit_activity") }}</option>
              </select>
            </div>
          </div>
        </div>
        <sweet-textarea
          v-model="form.name"
          :isValid="!$v.form.name.$error"
          @input="$v.form.name.$touch()"
          :rows="3"
          layout="vertical"
          :placeholder="$t('audit-control.control.description')"
          :label="$t('audit-control.control.description')"
        />
        <sweet-textarea
          class="mt-2"
          v-model="form.goal"
          :isValid="!$v.form.goal.$error"
          @input="$v.form.goal.$touch()"
          :rows="3"
          layout="vertical"
          :placeholder="$t('audit-control.control.goal')"
          :label="$t('audit-control.control.goal')"
        />

        <div class="row mt-2">
          <!-- Status & Category -->
          <div class="col-md-6" v-if="form._id">
            <div class="form-group">
              <label>{{ $t("audit-control.control.status") }}</label>
              <select :disabled="!form._id" class="form-control" v-model="form.status" :class="{ 'is-invalid': $v.form.status.$error }">
                <option :value="undefined">{{ $t("common.select") }}</option>
                <option value="ACTIVE">{{ $t("audit-control.status.active") }}</option>
                <option value="COMPLETED">{{ $t("audit-control.status.completed") }}</option>
                <option value="PASSIVE">{{ $t("audit-control.status.passive") }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Criticality & Type -->
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t("audit-control.control.criticality") }} <span class="text-danger">*</span></label>
              <select class="form-control" v-model="form.criticality" :class="{ 'is-invalid': $v.form.criticality.$error }" @blur="$v.form.criticality.$touch()">
                <option :value="undefined">{{ $t("common.select") }}</option>
                <option value="LOW">{{ $t("audit-control.criticality.low") }}</option>
                <option value="MEDIUM">{{ $t("audit-control.criticality.medium") }}</option>
                <option value="HIGH">{{ $t("audit-control.criticality.high") }}</option>
                <option value="CRITICAL">{{ $t("audit-control.criticality.critical") }}</option>
              </select>
              <div class="invalid-feedback" v-if="$v.form.criticality.$error">{{ $t("task.required-field-warning") }}</div>
            </div>
          </div>
          <div class="col-md-6">
            <right-panel
              class="float-right"
              @hidden="typePanelClosed"
              id="sidebar-new-control-type"
              :text="$t('audit-control.control.new-type')"
              width="600px"
              :icon="'mdi mdi-plus'"
            >
              <div class="pl-5 pr-5">
                <ControlTypeForm />
              </div>
            </right-panel>
            <div class="form-group">
              <label>{{ $t("audit-control.control.type") }}</label>
              <filter-select
                ref="controlTypeFilter"
                :selected="form.type"
                @filter="onControlTypeSearch"
                @changed="onControlTypeSelect"
                :placeholder="$t('audit-control.control.type')"
                optionLabel="name"
                :multiple="false"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Owner & Manager -->
          <div class="col-md-6">
            <label>{{ $t("audit-control.control.manager") }}</label>
            <organization-member-select class="d-block" :selected="form.manager" @onSelect="(val) => (form.manager = val)" />
            <!-- <sweet-input
              v-model="form.manager"
              :isValid="!$v.form.manager.$error"
              layout="vertical"
              :placeholder="$t('audit-control.control.manager')"
              :label="$t('audit-control.control.manager')"
            /> -->
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <right-panel
                class="float-right"
                @hidden="categoryPanelClosed"
                id="sidebar-new-control-category"
                :text="$t('audit-control.control.new-category')"
                width="600px"
                :icon="'mdi mdi-plus'"
              >
                <div class="pl-5 pr-5">
                  <ControlCategoryForm />
                </div>
              </right-panel>
              <label>{{ $t("audit-control.control.category") }} <span class="text-danger">*</span></label>
              <filter-select
                ref="controlCategoryFilter"
                v-model="form.category"
                :selected="form.category"
                @filter="onControlCategorySearch"
                @changed="onControlCategorySelect"
                :placeholder="$t('audit-control.control.category')"
                optionLabel="name"
                :multiple="false"
                :class="{ 'is-invalid': $v.form.category.$error }"
              />
              <div class="invalid-feedback" style="display: block" v-if="$v.form.category.$error">{{ $t("task.required-field-warning") }}</div>
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $t("audit-control.control.owner") }}</label>
              <select @change="ownerChanded" class="form-control" v-model="selectedOwnerType">
                <option value="ORGANIZATION">{{ $t("audit-control.control-level.corporate") }}</option>
                <option value="DEPARTMENT">{{ $t("department.text") }}</option>
                <option value="PROCESS">{{ $t("process.text") }}</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mt-4">
              <div v-if="selectedOwnerType == 'ORGANIZATION'">
                <!-- <h6>{{ $t("organization.text") }}</h6> -->
                <filter-select
                  disabled
                  v-show="currentOrganization"
                  class="mt-1"
                  :selected="{ _id: currentOrganization._id, name: { tr: currentOrganization.name, en: currentOrganization.name } }"
                  :placeholder="$t('organization.text')"
                  :multiple="false"
                  :custom-label="customLabel"
                />
              </div>
              <div v-if="selectedOwnerType == 'DEPARTMENT'">
                <filter-select
                  class="mt-1"
                  ref="departmentFilter"
                  :selected="form.owner ? form.owner.department : null"
                  :placeholder="$t('department.text')"
                  :multiple="false"
                  @filter="onDepartmentSearch"
                  @changed="onDepartmentSelect"
                />
              </div>
              <div v-if="selectedOwnerType == 'PROCESS'">
                <filter-select
                  class="mt-1"
                  ref="processFilter"
                  @filter="onProcessSearch"
                  :selected="form.owner ? form.owner.process : null"
                  :placeholder="$t('process.text')"
                  :multiple="false"
                  @changed="onProcessSelect"
                />
              </div>
            </div>
          </div>
        </div>

        <sweet-textarea v-model="form.riskNote" :rows="3" layout="vertical" placeholder="Risk Notu" label="Risk Notu" />
        <sweet-textarea
          class="mt-2"
          v-model="form.relatedStandards"
          :rows="3"
          layout="vertical"
          :placeholder="$t('audit-control.related-standards')"
          :label="$t('audit-control.related-standards')"
        />

        <div class="row" v-if="showPeriod">
          <div class="col-md-12">
            <label>{{ $t("audit-control.control.period") }} <span class="text-danger">*</span></label>
          </div>
          <div class="col-md-6">
            <sweet-input
              type="number"
              v-model="form.period.value"
              layout="vertical"
              :placeholder="$t('audit-control.control.period-value')"
              :isValid="!$v.form.period.value.$error"
              @input="$v.form.period.value.$touch()"
            />
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <select class="form-control" v-model="form.period.unit" :class="{ 'is-invalid': $v.form.period.unit.$error }">
                <option :value="undefined">{{ $t("common.select") }}</option>
                <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>{{ $t("audit-control.control.compliance-items") }}</label>

              <span v-b-modal="'compliance-item-modal-select'" class="text-primary">
                <i class="bx bx-search font-size-16 align-middle"></i>
                {{ $t("compliance-item.add") }}
              </span>
              <ComplianceItemSelect
                id="compliance-item-modal-select"
                multiple="true"
                :title="$t('compliance-item.add')"
                store="libraryComplianceItem"
                @selected="complianceItemSelected"
              />

              <table class="table table-bordered table-striped bg-white">
                <thead class="bg-secondary text-white">
                  <tr>
                    <td class="w-10">{{ $t("compliance-item.compliance") }}</td>
                    <td class="w-10">{{ $t("compliance-item-group.text") }}</td>
                    <td class="w-10">{{ $t("compliance-item.no") }}</td>
                    <td class="w-10">{{ $t("compliance-item.level") }}</td>
                    <td class="w-30">{{ $t("compliance-item.name") }}</td>
                    <td class="w-30">{{ $t("compliance-item.description") }}</td>
                    <td class="w-5"></td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in form.complianceItems" :key="item._id">
                    <td>{{ item.compliance }}</td>
                    <td>
                      <span v-if="item.group">{{ item.group.name | translate }}</span>
                    </td>
                    <td>{{ item.no }}</td>
                    <td>{{ item.level }}</td>
                    <td>{{ item.name | translate }}</td>
                    <td>{{ item.description | translate }}</td>
                    <td>
                      <i class="bx bx-trash hand text-danger font-size-18" @click="removeComplianceItem(item._id)"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <!-- <label>{{ $t("audit-control.control.risks") }}</label> -->
              <!-- <filter-select v-model="form.risks" storeModule="organizationRisk" :multiple="true" :placeholder="$t('common.select')" optionLabel="name" /> -->
              <!-- <filter-select :id="'riskFilter'" :ref="'riskFilter'" :multiple="true" :placeholder="$t('risk.text')" @filter="onRiskSearch" @changed="onRiskSelect($event)" /> -->
            </div>
          </div>
        </div>

        <!-- <div class="mt-5" style="gap:1rem">
        <div class="r-select-container">
          <h6>{{ $t("audit-control.approval.assignee") }}</h6>

          <organization-member-select class="d-block w-50" :selected="form.approval.assignee" @onSelect="(val) => (form.approval.assignee = val)" />
        </div>
        <div class="w-50" v-if="form.approval.assignee">
          <sweet-textarea v-model="form.approval.reporterNote" :rows="2" layout="vertical" :placeholder="$t('common.description')" :label="$t('common.description')" />
        </div>
      </div> -->
      </b-card>
      <!-- Approval Info -->
      <b-card :title="$t('audit-finding.section.approval')" class="mb-3">
        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('common.approver')">
              <organization-member-select
                v-if="form && form.approval"
                class="d-block w-50"
                :selected="form.approval.assignee"
                @onSelect="(val) => (form.approval.assignee = val)"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('common.reporter-note')">
              <sweet-textarea v-if="form && form.approval" v-model="form.approval.reporterNote" :rows="3" />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import OrganizationMemberSelect from "@cc/organization-member-select";
import { maxLength, minLength, required, requiredIf } from "vuelidate/lib/validators";
import SweetTextarea from "@c/form/sweet-textarea";
import SweetInput from "@c/form/sweet-input";
import ComplianceItemSelect from "./compliance-item-select.vue";
import ControlCategoryForm from "../library/workspace-library-control-category-form";
import ControlTypeForm from "../library/workspace-library-control-type-form";

const state = "organizationControl";

export default {
  components: {
    SweetTextarea,
    SweetInput,
    ComplianceItemSelect,
    OrganizationMemberSelect,
    ControlCategoryForm,
    ControlTypeForm,
  },
  props: {
    allowedClass: {
      type: String,
      default: undefined,
    },
    lockedClass: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedOwnerType: "ORGANIZATION",
      form: {
        no: undefined,
        goal: undefined,
        class: undefined,
        status: "ACTIVE", // Default
        category: undefined,
        criticality: undefined,
        type: undefined,
        owner: undefined,
        manager: undefined,
        name: undefined,
        riskNote: undefined,
        relatedStandards: undefined,
        owner: { organization: this.currentOrganization },
        period: {
          value: undefined,
          unit: undefined,
        },
        approval: {
          assignee: undefined,
        },
        complianceItems: [],
        risks: [],
      },
      formError: undefined,
    };
  },
  validations: {
    form: {
      category: { required },
      criticality: { required },
      name: {
        required, // Description field
        minLength: minLength(1),
        maxLength: maxLength(1000),
      },
      period: {
        value: {
          required: requiredIf(function() {
            return this.showPeriod;
          }),
        },
        unit: {
          required: requiredIf(function() {
            return this.showPeriod;
          }),
        },
      },
      manager: { required },
      class: {},
      goal: {},
      status: {},
      type: {},
      approval: {
        assignee: { required },
      },
    },
  },

  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v && v._id) {
          const entity = this.prepareFormForEdit(v, ["relatedStandards", "goal", "riskNote", "manager"]);
          // Ensure nested objects init
          if (!entity.period) entity.period = { value: undefined, unit: undefined };
          if (!entity.complianceItems) entity.complianceItems = [];
          if (!entity.risks) entity.risks = [];

          this.form = { ...entity };

          if (entity.owner) {
            if (entity.owner.department) this.selectedOwnerType = "DEPARTMENT";
            else if (entity.owner.process) this.selectedOwnerType = "PROCESS";
            else this.selectedOwnerType = "ORGANIZATION";
          } else {
            this.form.owner = { organization: this.currentOrganization };
            this.selectedOwnerType = "ORGANIZATION";
          }
        } else {
          this.resetForm();
        }
      },
    },
    allowedClass: {
      deep: true,
      handler(val) {
        if (val && !this.form._id) {
          this.$set(this.form, "class", val);
        }
      },
      immediate: true,
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    showPeriod() {
      return this.form.class === "COMPLIANCE_MANAGEMENT" || this.form.class === "CONTROL_ACTIVITY";
    },
  },

  methods: {
    categoryPanelClosed() {
      this.$refs["controlCategoryFilter"]?.reset();
    },
    typePanelClosed() {
      this.$refs["controlTypeFilter"]?.reset();
    },
    async onControlTypeSearch(value, callback) {
      const response = await this.$store.dispatch(`libraryControlType/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: {
          // "scope.value": { value: "system", isObject: true },
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onControlTypeSelect(value) {
      this.form.type = value;
    },
    async onControlCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`libraryControlCategory/paginate`, {
        options: { limit: 200, sort: { name: "asc" } },
        query: {
          // "scope.value": { value: "system", isObject: true },
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onControlCategorySelect(value) {
      this.form.category = value;
    },
    // async onRiskSearch(value, callback) {
    //   const response = await this.$store.dispatch(`organizationRisk/paginate`, {
    //     options: { limit: 200 },
    //     query: { name: { value }, status: { value: { $ne: "RATING" }, isObject: true }, products: { value: { $in: [this.product] }, isObject: true } },
    //   });

    //   callback(response.data?.docs ?? []);
    // },

    // onRiskSelect(risks) {
    //   this.form.risks = risks;
    //   this.$forceUpdate();
    // },

    ownerChanded(e) {
      if (e.target.value == "ORGANIZATION") {
        this.onOrganizationSelect();
      }
      setTimeout(() => {
        this.$refs?.departmentFilter?.reset();
        this.$refs?.processFilter?.reset();
      }, 500);
    },
    onOrganizationSelect() {
      const owner = { organization: this.currentOrganization };
      this.form = { ...this.form, owner };
    },
    customLabel(value) {
      return this.$options.filters.translate(value.name);
    },
    async onDepartmentSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDepartment/paginate`, {
        options: { limit: 200 },
        query: {
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onDepartmentSelect(department) {
      const owner = { department };
      this.form = { ...this.form, owner };
    },
    async onProcessSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationProcess/paginate`, {
        options: { limit: 200 },
        query: {
          name: { value },
        },
      });
      callback(response.data?.docs ?? []);
    },
    onProcessSelect(process) {
      const owner = { process };
      this.form = { ...this.form, owner };
    },

    complianceItemSelected(selections) {
      this.form = {
        ...this.form,
        complianceItems: [...selections],
      };
    },
    removeComplianceItem(id) {
      this.form.complianceItems = this.form.complianceItems.filter((item) => item._id !== id);
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        if (this.form.approval && this.form.approval.reporterNote) {
          this.form.approval.reporterNote = { tr: this.form.approval.reporterNote, en: this.form.approval.reporterNote };
        }
        if (this.form.relatedStandards) {
          this.form.relatedStandards = { tr: this.form.relatedStandards, en: this.form.relatedStandards };
        }
        const { data } = await this.$store.dispatch(`${state}/save`, this.form);
        this.resetForm();
        this.$v.$reset();
        this.$toast.success(this.$t("common.success"));
        if (this.closeOnSave) {
          this.$root.$emit("bv::toggle::collapse", "sidebar-new-control");
        }
        setTimeout(() => this.$emit("onSave", data), 500);
        // this.$emit("saved");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    resetForm() {
      this.selectedOwnerType = "ORGANIZATION";
      this.form = {
        no: undefined,
        class: this.allowedClass || "CONTROL_ACTIVITY",
        status: "ACTIVE",
        category: undefined,
        criticality: undefined,
        type: undefined,
        owner: undefined,
        manager: undefined,
        name: undefined,
        riskNote: undefined,
        owner: { organization: this.currentOrganization },
        relatedStandards: undefined,
        period: {
          value: undefined,
          unit: undefined,
        },
        complianceItems: [],
        risks: [],
        approval: {
          assignee: undefined,
          reporterNote: undefined,
        },
      };
    },
  },
};
</script>
